import ApiService from '@/services/api.service.js';
import logger from '@/utils/logger';

/**
 * Normalize transactions to a common structure
 * Uses the same normalization logic as VendorCenter.vue
 * @param {Array} bills - Array of bills
 * @param {Array} payBills - Array of pay bills
 * @param {Array} cheques - Array of cheques
 * @param {Array} vendorCredits - Array of vendor credit notes
 * @param {Array} cashPurchases - Array of cash purchases
 * @param {Array} expenses - Array of expenses
 * @returns {Array} Normalized transactions
 */
function normalizeTransactions(
  bills = [],
  payBills = [],
  cheques = [],
  vendorCredits = [],
  cashPurchases = [],
  expenses = [],
  cashExpenses = [],
  creditCardExpenses = [],
) {
  const normalize = t => {
    // Bill
    if (t.billNo) {
      return {
        id: t.id,
        date: t.date,
        type: 'Bill',
        number: t.billNo,
        memo: t.comments || '-',
        dueDate: t.dueDate || null,
        amount: t.total,
        status: t.status || '-',
        vendorId: t.vendorId || t.vendor_id || t.vendor?.id,
        account: t.bankAccount || '-',
        raw: t
      };
    }
    // Pay Bill
    if (t.no && (t.type === 'Pay Bill' || t.payBillNo || t.paymentMethod)) {
      return {
        id: t.id,
        date: t.date,
        type: 'Pay Bill',
        number: t.no,
        memo: t.comments || '-',
        dueDate: '',
        amount: t.total,
        status: 'Paid',
        vendorId: t.vendorId || t.vendor_id || t.vendor?.id,
        account: t.payFromAccountId,
        paymentMethod: t.paymentMethod || t.payMethod || 'Cash',
        raw: t
      };
    }
    // Cheque
    if (t.chequeNo) {
      return {
        id: t.id,
        date: t.date,
        type: 'Cheque',
        number: t.chequeNo,
        memo: t.comments || '-',
        dueDate: t.dueDate || '',
        amount: t.total,
        status: t.status || '',
        vendorId: t.vendorId || t.vendor_id || t.vendor?.id,
        account: t.bankAccount || '',
        raw: t
      };
    }
    // Vendor Credit Note
    if (t.creditNoteNo) {
      return {
        id: t.id,
        date: t.date,
        type: 'Vendor Credit Note',
        number: t.creditNoteNo,
        memo: t.comments || '-',
        dueDate: t.dueDate || '',
        amount: t.total,
        status: t.status || '',
        vendorId: t.vendorId || t.vendor_id || t.vendor?.id,
        account: t.bankAccount || '',
        raw: t
      };
    }
    // Cash Purchase
    if (t.type === 'Cash Purchase' || t.cashPurchaseNo) {
      return {
        id: t.id,
        date: t.date,
        type: 'Cash Purchase',
        number: t.number || t.cashPurchaseNo,
        memo: t.comments || '-',
        dueDate: t.dueDate || '',
        amount: t.total,
        status: t.status || '',
        vendorId: t.vendorId || t.vendor_id || t.vendor?.id,
        account: t.payFrom || '',
        raw: t
      };
    }
    // Direct vendor expense (cash or credit card charge)
    if (
      t.expenseKind === 'Cash Expense'
      || t.expenseKind === 'Credit Card Expense'
      || t.sourceType === 'cash-expense'
      || t.sourceType === 'credit-card-expense'
    ) {
      const expenseKind = t.expenseKind
        || (t.sourceType === 'cash-expense' ? 'Cash Expense' : 'Credit Card Expense');
      const prefix = expenseKind === 'Cash Expense' ? 'CE' : 'CCE';
      return {
        id: t.id,
        date: t.date,
        type: 'Expense',
        expenseKind,
        number: t.number || t.no || `${prefix}-${String(t.id).padStart(4, '0')}`,
        memo: t.comments || '-',
        dueDate: '',
        amount: t.total ?? t.originalAmount,
        status: 'Paid',
        vendorId: t.vendorId || t.vendor_id || t.vendor?.id,
        account: t.payFrom || '',
        raw: t,
      };
    }
    // Expense
    if (t.type === 'Expense' || t.expenseNo) {
      return {
        id: t.id,
        date: t.date,
        type: 'Expense',
        number: t.number || t.expenseNo,
        memo: t.comments || '-',
        dueDate: t.dueDate || '',
        amount: t.total,
        status: t.status || '',
        vendorId: t.vendorId || t.vendor_id || t.vendor?.id,
        account: t.payFrom || '',
        raw: t
      };
    }
    // Fallback
    return {
      id: t.id,
      date: t.date,
      type: t.type || 'Transaction',
      number: t.no || t.id,
      memo: t.comments || '-',
      dueDate: t.dueDate || '',
      amount: t.total,
      status: t.status || '',
      vendorId: t.vendorId || t.vendor_id || t.vendor?.id,
      account: t.account || '',
      raw: t
    };
  };

  return [
    ...bills.map(normalize),
    ...payBills.map(pb => normalize({ ...pb, type: 'Pay Bill' })),
    ...cheques.map(normalize),
    ...vendorCredits.map(normalize),
    ...cashPurchases.map(normalize),
    ...expenses.map(normalize),
    ...cashExpenses.map((row) => normalize({
      ...row,
      expenseKind: 'Cash Expense',
      sourceType: 'cash-expense',
    })),
    ...creditCardExpenses.map((row) => normalize({
      ...row,
      expenseKind: 'Credit Card Expense',
      sourceType: 'credit-card-expense',
    })),
  ];
}

/**
 * Fetch all transactions for vendors
 * @param {string|number} companyId - Company ID
 * @param {Array} vendors - Array of vendor objects
 * @returns {Promise<Object>} Object with all transaction types
 */
export async function fetchVendorTransactions(companyId, vendors) {
  try {
    // Filter transactions by vendor IDs
    const vendorIds = vendors.map(v => String(v.id || v.vendorId)).filter(Boolean);
    
    if (vendorIds.length === 0) {
      return {
        bills: [],
        payBills: [],
        cheques: [],
        vendorCredits: [],
        cashPurchases: [],
        expenses: [],
        cashExpenses: [],
        creditCardExpenses: [],
        allTransactions: [],
      };
    }

    // Fetch bills for all selected vendors
    let allBills = [];
    let allPayBills = [];
    let allCashExpenses = [];
    let allCreditCardExpenses = [];

    try {
      const expenseRows = await ApiService.getBillsAndExpenses(companyId, { view: 'Expenses' });
      const rows = Array.isArray(expenseRows) ? expenseRows : [];
      allCashExpenses = rows.filter((row) => row?.expenseKind === 'Cash Expense');
      allCreditCardExpenses = rows.filter((row) => row?.expenseKind === 'Credit Card Expense');
    } catch (err) {
      logger.warn('Could not fetch direct vendor expenses:', err);
    }
    
    for (const vendorId of vendorIds) {
      try {
        // Fetch bills for this vendor
        const billsResponse = await ApiService.getVendorBills(vendorId);
        if (Array.isArray(billsResponse)) {
          allBills.push(...billsResponse);
        }
        
        // Fetch pay bills for this vendor
        const payBillsResponse = await ApiService.get(`/vendor/pay-bill/vendor/${vendorId}`);
        const payBills = payBillsResponse?.data || [];
        if (Array.isArray(payBills)) {
          allPayBills.push(...payBills);
        }
      } catch (err) {
        logger.warn(`Could not fetch transactions for vendor ${vendorId}:`, err);
      }
    }

    // Filter by vendor IDs to ensure we only get transactions for selected vendors
    const filteredBills = allBills.filter(bill => {
      const billVendorId = String(bill.vendorId || bill.vendor_id || bill.vendor?.id || '');
      return vendorIds.includes(billVendorId);
    });
    
    const filteredPayBills = allPayBills.filter(payBill => {
      const payBillVendorId = String(payBill.vendorId || payBill.vendor_id || payBill.vendor?.id || '');
      return vendorIds.includes(payBillVendorId);
    });

    const filteredCashExpenses = allCashExpenses.filter((row) => {
      const vendorId = String(row.vendorId || row.vendor_id || '');
      return vendorIds.includes(vendorId);
    });

    const filteredCreditCardExpenses = allCreditCardExpenses.filter((row) => {
      const vendorId = String(row.vendorId || row.vendor_id || '');
      return vendorIds.includes(vendorId);
    });

    // For now, cheques, vendorCredits, cashPurchases, and legacy expenses are empty
    const cheques = [];
    const vendorCredits = [];
    const cashPurchases = [];
    const expenses = [];

    return {
      bills: filteredBills,
      payBills: filteredPayBills,
      cheques,
      vendorCredits,
      cashPurchases,
      expenses,
      cashExpenses: filteredCashExpenses,
      creditCardExpenses: filteredCreditCardExpenses,
      allTransactions: normalizeTransactions(
        filteredBills,
        filteredPayBills,
        cheques,
        vendorCredits,
        cashPurchases,
        expenses,
        filteredCashExpenses,
        filteredCreditCardExpenses,
      ),
    };
  } catch (err) {
    logger.error('Error fetching vendor transactions:', err);
    return {
      bills: [],
      payBills: [],
      cheques: [],
      vendorCredits: [],
      cashPurchases: [],
      expenses: [],
      cashExpenses: [],
      creditCardExpenses: [],
      allTransactions: []
    };
  }
}

/**
 * Export vendors with transactions to Excel
 * @param {Object} options - Export options
 * @param {Array} options.vendors - List of vendors to export
 * @param {Array} options.transactions - List of normalized transactions
 * @param {Object} options.accountsMap - Map of account IDs to account objects for formatting
 * @param {Function} options.showToast - Function to show toast notifications (optional)
 * @returns {Promise<void>}
 */
export async function exportVendorsWithTransactionsToExcel({
  vendors,
  transactions,
  accountsMap = {},
  showToast
}) {
  if (!vendors || vendors.length === 0) {
    if (showToast) showToast('warning', 'No vendors to export');
    return;
  }

  try {
    // Show loading message
    if (showToast) showToast('info', 'Preparing export...');

    // Ensure we have transactions array (default to empty array if not provided)
    const allTransactions = transactions || [];
    
    if (allTransactions.length === 0) {
      if (showToast) showToast('warning', 'No transactions to export');
      return;
    }
    
    // Log for debugging
    logger.info(`Exporting ${allTransactions.length} transaction(s)`);

    // Dynamically import xlsx library
    const XLSX = await import('xlsx');

    // Helper function to get vendor name (matching modal's getVendorName method)
    const getVendorName = (vendorId) => {
      const vendor = vendors.find(v => 
        String(v.id || v.vendorId) === String(vendorId)
      );
      return vendor?.vendorName || vendor?.name || '-';
    };

    // Helper function to format date (matching modal's formatDate method)
    const formatDate = (date) => {
      if (!date) return '-';
      try {
        return new Date(date).toLocaleDateString();
      } catch (e) {
        return date;
      }
    };

    // Helper function to format currency (matching modal's formatCurrency method)
    const formatCurrency = (amount) => {
      if (amount === null || amount === undefined || isNaN(amount) || amount === 0) {
        return '-';
      }
      return '$' + Number(amount).toFixed(2);
    };

    // Helper function to format account (matching modal's formatAccount method)
    const formatAccount = (accountId) => {
      if (!accountId) return '-';
      if (!accountsMap || typeof accountsMap !== 'object') return accountId;
      
      const acc = accountsMap[accountId];
      if (!acc) return accountId || '-';
      
      try {
        return [
          acc.accountNumber,
          acc.accountName,
          acc.accountType
        ].filter(Boolean).join(' - ') || accountId || '-';
      } catch (error) {
        return accountId || '-';
      }
    };

    // Group transactions by vendor, then by type
    const transactionsByVendor = {};
    allTransactions.forEach(transaction => {
      const vendorName = getVendorName(transaction.vendorId);
      const transactionType = transaction.type || 'Other';
      
      if (!transactionsByVendor[vendorName]) {
        transactionsByVendor[vendorName] = {};
      }
      if (!transactionsByVendor[vendorName][transactionType]) {
        transactionsByVendor[vendorName][transactionType] = [];
      }
      transactionsByVendor[vendorName][transactionType].push(transaction);
    });

    // Sort transactions within each type by date (newest first)
    Object.keys(transactionsByVendor).forEach(vendorName => {
      Object.keys(transactionsByVendor[vendorName]).forEach(type => {
        transactionsByVendor[vendorName][type].sort((a, b) => {
          const dateA = new Date(a.date || 0);
          const dateB = new Date(b.date || 0);
          return dateB - dateA;
        });
      });
    });

    // Build Excel data structure with grouping
    const transactionsData = [];
    
    // Sort vendors alphabetically
    const sortedVendorNames = Object.keys(transactionsByVendor).sort();
    
    sortedVendorNames.forEach(vendorName => {
      // Add vendor name row (only in Vendor column, others empty)
      transactionsData.push({
        'Vendor': vendorName,
        'Type': '',
        'Number': '',
        'Date': '',
        'Memo': '',
        'Due Date': '',
        'Amount': '',
        'Account': '',
        'Status': ''
      });
      
      // Get transaction types for this vendor and sort them
      const types = Object.keys(transactionsByVendor[vendorName]).sort();
      
      types.forEach(type => {
        // Add transaction type row (only in Type column, others empty)
        transactionsData.push({
          'Vendor': '',
          'Type': type,
          'Number': '',
          'Date': '',
          'Memo': '',
          'Due Date': '',
          'Amount': '',
          'Account': '',
          'Status': ''
        });
        
        // Add transaction detail rows for this type
        transactionsByVendor[vendorName][type].forEach(transaction => {
          transactionsData.push({
            'Vendor': '',
            'Type': '',
            'Number': transaction.number || '',
            'Date': formatDate(transaction.date),
            'Memo': transaction.memo || '-',
            'Due Date': formatDate(transaction.dueDate),
            'Amount': formatCurrency(transaction.amount),
            'Account': formatAccount(transaction.account),
            'Status': transaction.status || '-'
          });
        });
      });
      
      // Add empty row after each vendor for readability
      transactionsData.push({
        'Vendor': '',
        'Type': '',
        'Number': '',
        'Date': '',
        'Memo': '',
        'Due Date': '',
        'Amount': '',
        'Account': '',
        'Status': ''
      });
    });

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(transactionsData);
    
    // Merge cells for vendor name rows (spanning all columns)
    if (!ws['!merges']) {
      ws['!merges'] = [];
    }
    
    let currentRow = 1; // Start after header row (0-indexed, so row 1 is first data row)
    sortedVendorNames.forEach(vendorName => {
      const types = Object.keys(transactionsByVendor[vendorName]);
      const numRowsForVendor = 1 + types.length + types.reduce((sum, type) => 
        sum + transactionsByVendor[vendorName][type].length, 0) + 1; // vendor row + type rows + transaction rows + empty row
      
      // Merge vendor name cell across all columns
      ws['!merges'].push({
        s: { r: currentRow, c: 0 },
        e: { r: currentRow, c: 8 } // 9 columns (0-8): Vendor, Type, Number, Date, Memo, Due Date, Amount, Account, Status
      });
      
      currentRow += numRowsForVendor;
    });

    // Set column widths
    const cols = [
      { wch: 30 }, // Vendor
      { wch: 20 }, // Type
      { wch: 20 }, // Number
      { wch: 15 }, // Date
      { wch: 40 }, // Memo
      { wch: 15 }, // Due Date
      { wch: 15 }, // Amount
      { wch: 30 }, // Account
      { wch: 15 }  // Status
    ];
    ws['!cols'] = cols;

    // Create workbook with single sheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Transactions');

    // Generate filename
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `Vendor_Transactions_${timestamp}.xlsx`;

    // Write workbook to binary string
    const wbout = XLSX.write(wb, { 
      bookType: 'xlsx', 
      type: 'array',
      bookSST: false
    });

    // Create blob with proper MIME type
    const blob = new Blob([wbout], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });

    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    
    // Append to body before clicking
    document.body.appendChild(link);
    
    // Trigger download
    link.click();
    
    // Clean up after a short delay
    setTimeout(() => {
      if (link.parentNode) {
        document.body.removeChild(link);
      }
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);
    }, 100);

    // Show success message
    const message = `Exported ${allTransactions.length} transaction(s)`;
    if (showToast) showToast('success', message);
  } catch (err) {
    logger.error('Error exporting vendors with transactions to Excel:', err);
    if (showToast) showToast('error', 'Failed to export vendors with transactions to Excel');
    throw err;
  }
}

